import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { searchLoanDiskForManualLegal, manualAddLegalCase } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// ---------------------------------------------------------------------------
// FindLegalCaseDialog — the "Find Legal Case" button on the Legal Case
// Tracker opens this. Two-step flow mirroring the Bad Debt EditLoanDialog:
//   1. Search LoanDisk by SF# or borrower name → results table.
//   2. Click a row → POST /manual-add. If the row is already in the portal
//      as `open_loan`, the backend returns `requires_confirm:true` — we
//      show a bright yellow warning + a "Confirm & escalate" button that
//      re-POSTs with `force:true`.
// ---------------------------------------------------------------------------

const fmtGBPdp = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// LD status label → tone for the results table pill.
const STATUS_TONE = {
  "Active":       "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Open":         "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Legal":        "bg-red-50 text-red-700 border-red-200",
  "Closed":       "bg-slate-100 text-slate-600 border-slate-200",
  "Restructured": "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export default function FindLegalCaseDialog({ open, onOpenChange, onCaseCreated }) {
  const navigate = useNavigate();
  const [q, setQ]                       = useState("");
  const [searching, setSearching]       = useState(false);
  const [results, setResults]           = useState([]);
  const [busyLoanId, setBusyLoanId]     = useState(null);
  // Confirm-warning state — set when the backend replies requires_confirm.
  // { row, message, current_stage, handler, in_latest_arrears }
  const [confirmPayload, setConfirmPayload] = useState(null);

  const reset = () => {
    setQ("");
    setResults([]);
    setBusyLoanId(null);
    setConfirmPayload(null);
    setSearching(false);
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || q.trim().length < 2) {
      toast.error("Type at least 2 characters (SF# or customer name)");
      return;
    }
    try {
      setSearching(true);
      setConfirmPayload(null);
      const r = await searchLoanDiskForManualLegal(q.trim());
      setResults(r.results || []);
      if ((r.results || []).length === 0) {
        toast.info("No LoanDisk matches");
      }
    } catch (err) {
      toast.error("LoanDisk search failed: " + errorDetailMessage(err, err.message));
    } finally {
      setSearching(false);
    }
  };

  // Shared success handler for both first-try and force=true escalation.
  const finishEscalation = (row, r) => {
    // Point 3 of the ops spec — LD status must flip to Legal (3). If the
    // LD push failed (LoanDisk's API silently no-ops on status changes,
    // so read-verify caught it), tell ops loudly with a persistent warning
    // toast + a copy-friendly instruction.
    if (r.ld_pushed) {
      toast.success(
        r.was_existing
          ? `Escalated ${row.borrower_name} (${row.loan_application_id}) — was ${r.previous_stage || "open_loan"}. LoanDisk updated to Legal.`
          : `Created legal case for ${row.borrower_name} (${row.loan_application_id}). LoanDisk updated to Legal.`,
      );
    } else {
      toast.warning(
        `Portal escalated for ${row.borrower_name} (${row.loan_application_id}) — BUT LoanDisk was not updated. Open LoanDisk and change ${row.loan_application_id} to Legal (status_id=3) manually.`,
        { duration: 15000 },
      );
      // Also log the exact reason for debugging
      console.warn("[find-legal-case] LD push failed:", r.ld_push_message);
    }
    onCaseCreated?.({ ...row, case_id: r.case_id });
    onOpenChange(false);
    reset();
    // Navigate straight into the newly-legal case so ops can start work.
    navigate(`/legal/case/${r.case_id}?source=legal`);
  };

  const pickLoan = async (row, force = false) => {
    try {
      setBusyLoanId(row.loan_id);
      const payload = {
        loan_id:             row.loan_id,
        loan_application_id: row.loan_application_id,
        customer_name:       row.borrower_name,
        balance_amount:      row.balance_amount,
        principal_amount:    row.principal_amount,
        days_past_due:       row.days_past_due,
        force,
      };
      const r = await manualAddLegalCase(payload);

      if (r.requires_confirm) {
        setConfirmPayload({
          row,
          message:           r.message,
          current_stage:     r.current_stage,
          handler:           r.handler,
          in_latest_arrears: r.in_latest_arrears,
        });
        return;
      }
      finishEscalation(row, r);
    } catch (err) {
      toast.error("Escalate failed: " + errorDetailMessage(err, err.message));
    } finally {
      setBusyLoanId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}
    >
      <DialogContent className="max-w-4xl" data-testid="find-legal-case-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            Find & escalate a case to Legal
          </DialogTitle>
          <DialogDescription>
            Search LoanDisk by SF number or customer name. Picking a row creates
            (or promotes) the case in the Legal Case Tracker and pushes{" "}
            <span className="font-mono">loan_status_id&nbsp;=&nbsp;3&nbsp;(Legal)</span>{" "}
            back to LoanDisk.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={runSearch} className="flex gap-2">
          <Input
            autoFocus
            placeholder="Search LoanDisk — SF-12345 or 'Acme Ltd' (min 2 chars)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="find-legal-case-search-input"
          />
          <Button
            type="submit"
            size="sm"
            disabled={searching}
            data-testid="find-legal-case-search-btn"
          >
            {searching
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Search className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Search</span>
          </Button>
        </form>

        {searching && results.length === 0 && (
          <div className="py-8 text-center text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Searching LoanDisk…
          </div>
        )}

        {!searching && results.length === 0 && q && (
          <div className="py-8 text-center text-xs text-muted-foreground">
            No results — try a different SF# or borrower name.
          </div>
        )}

        {results.length > 0 && (
          <>
            <div className="text-[11px] text-muted-foreground -mb-1">
              {results.length} match{results.length === 1 ? "" : "es"} for{" "}
              <span className="font-semibold text-foreground">&ldquo;{q}&rdquo;</span>
            </div>
            <div className="overflow-x-auto max-h-[440px] border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-muted-foreground uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Borrower</th>
                    <th className="text-left px-2 py-1.5 font-semibold">SF #</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Released</th>
                    <th className="text-left px-2 py-1.5 font-semibold">LD status</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Principal</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Balance</th>
                    <th className="text-right px-2 py-1.5 font-semibold">DPD</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => {
                    const isAlreadyLegal = row.loan_status_label === "Legal";
                    return (
                      <tr
                        key={row.loan_id}
                        className={`border-t hover:bg-slate-50 ${isAlreadyLegal ? "bg-red-50/30" : ""}`}
                        data-testid={`find-legal-case-row-${row.loan_application_id}`}
                      >
                        <td className="px-2 py-1.5">{row.borrower_name}</td>
                        <td className="px-2 py-1.5 font-mono">{row.loan_application_id}</td>
                        <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap text-[10px]">
                          {row.released_date || "—"}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className={`text-[9px] ${STATUS_TONE[row.loan_status_label] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                            {row.loan_status_label}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmtGBPdp(row.principal_amount)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmtGBPdp(row.balance_amount)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{row.days_past_due || 0}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button
                            size="sm"
                            variant={isAlreadyLegal ? "ghost" : "outline"}
                            disabled={busyLoanId === row.loan_id || isAlreadyLegal}
                            onClick={() => pickLoan(row, false)}
                            className="h-7 px-2 text-[11px]"
                            data-testid={`find-legal-case-pick-${row.loan_application_id}`}
                            title={isAlreadyLegal
                              ? "Already flagged as Legal on LoanDisk — use the tracker's regular Refresh flow to sync it"
                              : "Escalate this loan to Legal"}
                          >
                            {busyLoanId === row.loan_id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : isAlreadyLegal
                                ? "Already legal"
                                : <>Escalate <ArrowRight className="w-3 h-3 ml-1 inline" /></>}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Confirmation warning shown when backend requires_confirm ── */}
        {confirmPayload && (
          <div
            className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 text-sm"
            data-testid="find-legal-case-confirm-warning"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="font-semibold text-amber-900">
                  Already in portal — confirm escalation
                </div>
                <div className="text-amber-900 text-[13px] leading-relaxed">
                  {confirmPayload.message}
                </div>
                <div className="text-[11px] text-amber-800 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    stage: <span className="font-mono ml-1">{confirmPayload.current_stage}</span>
                  </Badge>
                  {confirmPayload.handler && (
                    <Badge variant="outline" className="text-[10px]">
                      handler: {confirmPayload.handler}
                    </Badge>
                  )}
                  {confirmPayload.in_latest_arrears && (
                    <Badge variant="outline" className="text-[10px] bg-white">
                      in today&apos;s arrears list
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    className="bg-red-700 hover:bg-red-800 text-white h-8 text-xs"
                    onClick={() => pickLoan(confirmPayload.row, true)}
                    disabled={busyLoanId === confirmPayload.row.loan_id}
                    data-testid="find-legal-case-confirm-btn"
                  >
                    {busyLoanId === confirmPayload.row.loan_id
                      ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      : null}
                    Confirm — escalate to Legal
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs"
                    onClick={() => setConfirmPayload(null)}
                    data-testid="find-legal-case-confirm-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
